import { getTranslation } from '@mahawi/eshop-common/dist/src/translation';
import {
  EPropertyType,
  type ILanguageType,
  type IProperty,
  type IPropertyFilter,
  type IPropertyValue,
} from '@mahawi/eshop-common/dist/src/types';
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

function PropertyFilters({
  properties,
  filters,
  filteredTotal,
  availableValues,
  languageType,
  onChange,
  onDraftChange,
  brandFilter,
  stockFilter,
  featureFlagFilter,
}: {
  properties: IProperty[];
  filters: IPropertyFilter[];
  filteredTotal: number | null;
  availableValues: Record<string, string[]> | null;
  languageType: ILanguageType;
  onChange: (filters: IPropertyFilter[]) => void;
  onDraftChange: (draft: IPropertyFilter[]) => void;
  brandFilter?: ReactElement | null;
  stockFilter?: ReactElement | null;
  featureFlagFilter?: ReactElement | null;
}): ReactElement | null {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<IPropertyFilter[]>(filters);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;

  useEffect((): void => {
    setDraft(filters);
  }, [filters]);

  useEffect((): (() => void) => {
    if (draft.length === 0) {
      setIsCountLoading(false);
      onDraftChangeRef.current([]);
      return (): void => {};
    }

    setIsCountLoading(true);

    const timer = setTimeout((): void => {
      onDraftChangeRef.current(draft);
    }, 400);

    return (): void => {
      clearTimeout(timer);
    };
  }, [draft]);

  useEffect((): void => {
    if (filteredTotal !== null) {
      setIsCountLoading(false);
    }
  }, [filteredTotal]);

  const getDraft = useCallback(
    (code: string): IPropertyFilter | undefined =>
      draft.find((f: IPropertyFilter): boolean => f.code === code),
    [draft],
  );

  const upsertDraft = useCallback((updated: IPropertyFilter): void => {
    setDraft((prev: IPropertyFilter[]): IPropertyFilter[] => {
      const next = prev.filter(
        (f: IPropertyFilter): boolean => f.code !== updated.code,
      );
      next.push(updated);
      return next;
    });
  }, []);

  const removeDraft = useCallback((code: string): void => {
    setDraft((prev: IPropertyFilter[]): IPropertyFilter[] =>
      prev.filter((f: IPropertyFilter): boolean => f.code !== code),
    );
  }, []);

  const handleConfirm = useCallback((): void => {
    onChange(draft);
  }, [draft, onChange]);

  const handleReset = useCallback((): void => {
    onChange([]);
  }, [onChange]);

  const propertyLabel = (property: IProperty): string => {
    const name = getTranslation(property.names, languageType) ?? property.code;
    return property.unit ? `${name} [${property.unit}]` : name;
  };

  const renderBooleanFilter = (property: IProperty): ReactElement => {
    const active = getDraft(property.code);
    const checked = active?.booleanValue === true;
    const label = propertyLabel(property);

    return (
      <div className="pf-group" key={property.code}>
        <label className="pf-bool">
          <input
            type="checkbox"
            className="pf-bool__input"
            checked={checked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              if (e.target.checked) {
                upsertDraft({
                  code: property.code,
                  type: EPropertyType.BOOLEAN,
                  booleanValue: true,
                });
              } else {
                removeDraft(property.code);
              }
            }}
          />
          <span className="pf-bool__label">{label}</span>
        </label>
      </div>
    );
  };

  const renderEnumFilter = (property: IProperty): ReactElement | null => {
    const active = getDraft(property.code);
    const selectedCodes = active?.valuesCodes ?? [];
    const label = propertyLabel(property);

    const available = availableValues?.[property.code];

    const values = (property.values ?? [])
      .filter((pv: IPropertyValue): boolean => {
        if (available === undefined) {
          return true;
        }
        return selectedCodes.includes(pv.code) || available.includes(pv.code);
      })
      .slice()
      .sort((a: IPropertyValue, b: IPropertyValue): number => {
        const aLabel = getTranslation(a.names, languageType) ?? a.code;
        const bLabel = getTranslation(b.names, languageType) ?? b.code;
        const aNum = Number(aLabel);
        const bNum = Number(bLabel);

        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return aNum - bNum;
        }

        return aLabel.localeCompare(bLabel);
      });

    if (values.length <= 1 && selectedCodes.length === 0) {
      return null;
    }

    return (
      <div className="pf-group" key={property.code}>
        <p className="pf-group__label">{label}</p>
        <div className="pf-chips">
          {values.map((pv: IPropertyValue): ReactElement => {
            const isSelected = selectedCodes.includes(pv.code);
            const valueLabel =
              getTranslation(pv.names, languageType) ?? pv.code;

            return (
              <button
                key={pv.code}
                type="button"
                className={`pf-chip${isSelected ? ' pf-chip--active' : ''}`}
                aria-pressed={isSelected}
                onClick={(): void => {
                  const next = isSelected
                    ? selectedCodes.filter(
                        (c: string): boolean => c !== pv.code,
                      )
                    : [...selectedCodes, pv.code];

                  if (next.length === 0) {
                    removeDraft(property.code);
                  } else {
                    upsertDraft({
                      code: property.code,
                      type: EPropertyType.ENUM,
                      valuesCodes: next,
                    });
                  }
                }}
              >
                {valueLabel}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderNumberFilter = (property: IProperty): ReactElement => {
    const active = getDraft(property.code);
    const label = propertyLabel(property);

    const handleChange = (field: 'min' | 'max', rawValue: string): void => {
      const parsed = rawValue === '' ? undefined : Number(rawValue);

      const next: IPropertyFilter = {
        code: property.code,
        type: EPropertyType.NUMBER,
        min: field === 'min' ? parsed : active?.min,
        max: field === 'max' ? parsed : active?.max,
      };

      if (next.min === undefined && next.max === undefined) {
        removeDraft(property.code);
      } else {
        upsertDraft(next);
      }
    };

    return (
      <div className="pf-group" key={property.code}>
        <p className="pf-group__label">{label}</p>
        <div className="pf-range">
          <input
            type="number"
            className="pf-range__input"
            placeholder={t('products.filter.min')}
            value={active?.min ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              handleChange('min', e.target.value)
            }
          />
          <span className="pf-range__sep" aria-hidden="true">
            –
          </span>
          <input
            type="number"
            className="pf-range__input"
            placeholder={t('products.filter.max')}
            value={active?.max ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              handleChange('max', e.target.value)
            }
          />
        </div>
      </div>
    );
  };

  const hasDraftFilters = draft.length > 0;

  const renderedProperties = properties
    .slice()
    .sort((a: IProperty, b: IProperty): number =>
      propertyLabel(a).localeCompare(propertyLabel(b)),
    )
    .map((property: IProperty): ReactElement | null => {
      switch (property.type) {
        case EPropertyType.BOOLEAN:
          return renderBooleanFilter(property);
        case EPropertyType.ENUM:
          return renderEnumFilter(property);
        case EPropertyType.NUMBER:
          return renderNumberFilter(property);
        case EPropertyType.STRING:
          return null;
        default:
          return null;
      }
    });

  const hasVisibleContent =
    Boolean(brandFilter) ||
    Boolean(stockFilter) ||
    Boolean(featureFlagFilter) ||
    renderedProperties.some(
      (property: ReactElement | null): boolean => property !== null,
    );

  if (!hasVisibleContent) {
    return null;
  }

  return (
    <section className="pf-panel" aria-label={t('products.filter.properties')}>
      <h2 className="pf-panel__title">{t('products.filter.properties')}</h2>

      <div className="pf-panel__body">
        {brandFilter}
        {stockFilter}
        {featureFlagFilter}
        {renderedProperties}
      </div>

      <div className="pf-panel__footer">
        {hasDraftFilters && (
          <button
            type="button"
            className="pf-btn pf-btn--ghost"
            onClick={handleReset}
          >
            {t('products.filter.reset')}
          </button>
        )}
        <button
          type="button"
          className="pf-btn pf-btn--primary"
          onClick={handleConfirm}
        >
          {isCountLoading ? (
            <span className="pf-btn__loader" aria-hidden="true" />
          ) : filteredTotal !== null ? (
            t('products.filter.productCount', { count: filteredTotal })
          ) : (
            t('products.filter.show')
          )}
        </button>
      </div>
    </section>
  );
}

export default PropertyFilters;
